import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type { DebtCaseDecisionRequest, DebtCaseDecisionResponse, StaffDebtCase } from "../../api/staffTypes";
import { useStaffSession } from "../../auth/guards";
import { CursorListView } from "../../components/CursorListView";
import { Banner, Button, Card, EmptyState, Input, Money, PageHeader, QueryView, RadioGroup, StatusPill, Table, Textarea } from "../../components/ui";
import { staffKeys } from "../../layout/StaffShell";
import { addDecimal } from "../../lib/money";
import { useCursorList } from "../../lib/useCursorList";
import {
  AwaitingSecondBanner,
  DualControlProgress,
  IdLookup,
  MoneyConfirmDialog,
  Mono,
  parseEvidence,
  reasonError,
  shortId,
  StaffRejection,
  StepUpNote,
  useStaffCommand,
  when,
} from "./staffCommon";

// The adjudication console for publisher debt cases (ARCHITECTURE.md §9,
// migrations 0036/0037/0068; debt_adjudication.go). Cases are opened by
// the safety fan-out, never here. Migration 0068 (G-49/G-53/G-54/G-55/
// G-56) added GET /v1/internal/publisher-debt-cases?state= (a queue, so
// a case no longer has to be opened only by an id someone already has),
// made adjudicate and appeal/decide need a recent MFA step-up, made
// every write idempotent, replaced the untyped 400 request_rejected with
// typed refusals (actor_window_cap_exceeded, adjudicator_cannot_decide_appeal,
// reviewer_already_decided, case_state_not_writable — see staffCommon's
// STAFF_CODE_MESSAGE), and added `exposure` (the viewing reviewer's own
// 30-day approval total and the case's publisher's 90-day total, live)
// to every case read.

const STATE_TONE: Record<string, "info" | "success" | "warning" | "neutral" | "error"> = {
  pending_review: "warning",
  approved: "error",
  rejected: "success",
  appealed: "warning",
  appeal_upheld: "success",
  appeal_denied: "error",
};

export function StaffDebtCasesPage() {
  const ep = useEndpoints();
  const navigate = useNavigate();
  const [state, setState] = useState<"pending_review" | "appealed" | "all">("pending_review");
  const list = useCursorList(["staff-debt-cases", state], (cursor) => ep.staff.debtCases.list(state, cursor), (p) => p.cases);
  return (
    <div className="stack-lg">
      <PageHeader title="Publisher debt cases" description="Adjudicate a case opened when a safety finding invalidated a delivery a payout had already claimed." />
      <Card
        title="Cases"
        actions={
          <RadioGroup
            legend="Filter"
            name="debt-case-state"
            value={state}
            onChange={setState}
            options={[
              { value: "pending_review", label: "Pending review" },
              { value: "appealed", label: "Appealed" },
              { value: "all", label: "All" },
            ]}
          />
        }
      >
        <CursorListView list={list} loadingLabel="Loading debt cases" empty={<EmptyState title="No cases">No cases match this filter.</EmptyState>}>
          {(rows) => (
            <Table
              caption="Publisher debt cases"
              captionHidden
              rows={rows}
              rowKey={(c) => c.case_id}
              columns={[
                { key: "id", header: "Case", cell: (c) => <Link to={`/staff/debt-cases/${c.case_id}`}>{shortId(c.case_id)}</Link> },
                { key: "amt", header: "Claimed", numeric: true, cell: (c) => <Money value={c.claimed_amount} currency={c.currency} /> },
                { key: "state", header: "State", cell: (c) => <StatusPill tone={STATE_TONE[c.state] ?? "neutral"}>{c.state.replace(/_/g, " ")}</StatusPill> },
                { key: "dual", header: "Dual control", cell: (c) => (c.dual_approval_required ? "Two reviewers" : "One reviewer") },
                { key: "opened", header: "Opened", cell: (c) => when(c.opened_at) },
              ]}
            />
          )}
        </CursorListView>
      </Card>
      <Card title="Open by id">
        <IdLookup label="Case id" onOpen={(id) => navigate(`/staff/debt-cases/${id}`)} hint="From the publisher's case notice, a transfer reversal, or a write-off." />
      </Card>
    </div>
  );
}

export function StaffDebtCasePage() {
  const { caseId = "" } = useParams();
  const ep = useEndpoints();
  const q = useQuery({ queryKey: staffKeys.debtCase(caseId), queryFn: () => ep.staff.debtCases.get(caseId) });
  return (
    <div className="stack-lg">
      <PageHeader title={`Debt case ${shortId(caseId)}`} back={{ to: "/staff/debt-cases", label: "Debt cases" }} />
      <QueryView query={q} loadingLabel="Loading the case">
        {(c) => <CaseDetail c={c} />}
      </QueryView>
    </div>
  );
}

function CaseDetail({ c }: { c: StaffDebtCase }) {
  const adjudication = c.approvals.filter((a) => a.phase === "adjudication");
  const appeal = c.approvals.filter((a) => a.phase === "appeal");
  const required = c.dual_approval_required ? 2 : 1;
  const approveCount = (xs: typeof adjudication) => xs.filter((a) => a.decision === "approve").length;
  return (
    <div className="stack">
      <Card title="Case" actions={<StatusPill tone={STATE_TONE[c.state] ?? "neutral"}>{c.state.replace(/_/g, " ")}</StatusPill>}>
        <dl className="definition-list">
          <dt>Claimed amount</dt>
          <dd>
            <Money value={c.claimed_amount} currency={c.currency} />
          </dd>
          <dt>Publisher / advertiser</dt>
          <dd className="mono">
            {c.publisher_id} / {c.advertiser_id}
          </dd>
          <dt>Delivery</dt>
          <dd className="mono">{c.reservation_id}</dd>
          <dt>Dual control</dt>
          <dd>
            {c.dual_approval_required ? "Two distinct reviewers" : "One reviewer"}
            {c.cumulative_escalated ? " — escalated: the publisher's 90-day total would pass 2,500" : c.dual_approval_required ? " — more than 100" : ""}
          </dd>
          <dt>Opened / resolved</dt>
          <dd>
            {when(c.opened_at)} / {when(c.resolved_at)}
          </dd>
          {c.resolved_at ? (
            <>
              <dt>Funding</dt>
              <dd>{c.resolution_funding_source ?? "—"}</dd>
              <dt>Clawed back / debt / allowance</dt>
              <dd>
                <Money value={c.clawback_amount ?? "0"} currency={c.currency} /> / <Money value={c.debt_recognized ?? "0"} currency={c.currency} /> /{" "}
                <Money value={c.allowance_provisioned ?? "0"} currency={c.currency} />
              </dd>
              <dt>Advertiser credited</dt>
              <dd>
                <Money value={c.advertiser_credited ?? "0"} currency={c.currency} />
              </dd>
            </>
          ) : null}
        </dl>
        {c.state === "approved" || c.state === "appeal_denied" ? (
          <p className="small">
            The debt stands. <Link to={`/staff/write-offs/publisher-debt/${c.case_id}`}>Write-off options for this case</Link>
          </p>
        ) : null}
      </Card>
      <Card title="Exposure (§5)" subtitle="Computed live, not just the open-time snapshot (G-56).">
        <dl className="definition-list">
          <dt>Your 30-day approval total</dt>
          <dd>
            <Money value={c.exposure.actor_window_total} currency={c.currency} /> of <Money value={c.exposure.actor_window_cap} currency={c.currency} /> cap
          </dd>
          <dt>Publisher's 90-day total</dt>
          <dd>
            <Money value={c.exposure.publisher_window_total} currency={c.currency} /> of <Money value={c.exposure.publisher_window_cap} currency={c.currency} /> cap
          </dd>
        </dl>
      </Card>
      <Card title="Provisional holds">
        <Table
          caption="Holds on the claiming payouts"
          captionHidden
          rows={c.holds}
          rowKey={(h) => h.hold_id}
          empty={<p className="muted small">No holds.</p>}
          columns={[
            { key: "payout", header: "Payout", cell: (h) => <Mono>{shortId(h.payout_id)}</Mono> },
            { key: "cov", header: "Covered", numeric: true, cell: (h) => <Money value={h.covered_amount} currency={c.currency} /> },
            { key: "claims", header: "Claims", numeric: true, cell: (h) => h.claim_ids?.length ?? 0 },
            { key: "res", header: "Resolution", cell: (h) => (h.resolution ? `${h.resolution} (${when(h.resolved_at)})` : "Open") },
          ]}
        />
      </Card>
      <Card title="Approvals">
        <div className="stack">
          <DualControlProgress
            label="Adjudication approvals"
            approvals={approveCount(adjudication)}
            required={required}
            votes={adjudication.map((a) => ({ actor_user_id: a.actor_user_id, decision: a.decision, decided_at: a.decided_at }))}
          />
          {appeal.length || c.state === "appealed" ? (
            <DualControlProgress
              label="Appeal approvals"
              approvals={approveCount(appeal)}
              required={required}
              votes={appeal.map((a) => ({ actor_user_id: a.actor_user_id, decision: a.decision, decided_at: a.decided_at }))}
            />
          ) : null}
        </div>
      </Card>
      <Card title="Evidence" subtitle="Append-only.">
        {c.evidence.length ? (
          <ul className="stack-sm">
            {c.evidence.map((e, i) => (
              <li key={`${e.recorded_at}-${i}`}>
                <strong>{e.kind}</strong> · {e.recorded_by ? <Mono>{shortId(e.recorded_by)}</Mono> : "system"} · {when(e.recorded_at)}
                <pre className="staff-inert">{JSON.stringify(e.body, null, 2)}</pre>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">No evidence recorded.</p>
        )}
        <EvidenceForm caseId={c.case_id} />
      </Card>
      {c.state === "pending_review" ? <AdjudicateForm c={c} /> : null}
      {c.state === "approved" ? <FileAppealForm caseId={c.case_id} /> : null}
      {c.state === "appealed" ? <DecideAppealForm c={c} /> : null}
    </div>
  );
}

function useCaseRefresh(caseId: string) {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: staffKeys.debtCase(caseId) });
}

function EvidenceForm({ caseId }: { caseId: string }) {
  const ep = useEndpoints();
  const refresh = useCaseRefresh(caseId);
  const [kind, setKind] = useState("");
  const [body, setBody] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand((req: { kind: string; body: Record<string, unknown> }, opts) => ep.staff.debtCases.recordEvidence(caseId, req, opts), () => {
    setKind("");
    setBody("");
    refresh();
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    if (!kind.trim()) return setFormError("Give the evidence a kind, like payout_trace or publisher_statement.");
    const ev = parseEvidence(body, { required: true });
    if (!ev.ok) return setFormError(ev.error);
    setFormError(null);
    cmd.run({ kind: kind.trim(), body: ev.value });
  }
  return (
    <form className="stack-sm staff-subform" onSubmit={submit} noValidate>
      <h3 className="small">Record evidence</h3>
      <Input label="Kind" value={kind} onChange={(e) => setKind(e.target.value)} />
      <Textarea label="Body (JSON object)" className="mono" rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
      {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
      {cmd.error ? <StaffRejection error={cmd.error} /> : null}
      {cmd.data ? <Banner tone="success" live="polite" title="Evidence recorded" /> : null}
      <div>
        <Button type="submit" variant="secondary" loading={cmd.isPending}>
          Record evidence
        </Button>
      </div>
    </form>
  );
}

function outcomeBanner(res: DebtCaseDecisionResponse): ReactNode {
  if (res.result === "awaiting_second_approval") return <AwaitingSecondBanner approvals={res.approvals} required={res.approvals_required} />;
  if (!res.applied) return <Banner tone="warning" live="polite" title={`Not applied: ${res.result.replace(/_/g, " ")}`}>{res.state ? `The case is ${res.state.replace(/_/g, " ")}.` : null}</Banner>;
  return <Banner tone="success" live="polite" title={`Recorded: ${res.result.replace(/_/g, " ")}`} />;
}

function AdjudicateForm({ c }: { c: StaffDebtCase }) {
  const ep = useEndpoints();
  const refresh = useCaseRefresh(c.case_id);
  const { me } = useStaffSession();
  const [decision, setDecision] = useState<"approve" | "reject" | "">("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<DebtCaseDecisionRequest | null>(null);
  const cmd = useStaffCommand((req: DebtCaseDecisionRequest, opts) => ep.staff.debtCases.adjudicate(c.case_id, req, opts), () => {
    setConfirming(null);
    refresh();
  });
  const already = c.approvals.some((a) => a.phase === "adjudication" && a.actor_user_id === me.user_id);
  const approvalsIn = c.approvals.filter((a) => a.phase === "adjudication" && a.decision === "approve").length;
  const firstOfTwo = c.dual_approval_required && approvalsIn === 0;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!decision) return setFormError("Choose approve or reject.");
    const re = reasonError(notes);
    if (re) return setFormError(re);
    setFormError(null);
    cmd.reset();
    setConfirming({ decision, notes: notes.trim() });
  }

  const effect =
    confirming?.decision === "reject" ? (
      <>
        Decides for the publisher: their entitlement stands (still-held payout money goes back to their claimable balance) and the
        platform funds the advertiser's make-whole of <Money value={c.claimed_amount} currency={c.currency} /> as a loss. One reviewer
        resolves it.
      </>
    ) : (
      <>
        Decides against the publisher: the advertiser is credited <Money value={c.claimed_amount} currency={c.currency} />, funded by
        clawing back the payout money still held; any part of a payout that already paid becomes publisher debt, fully provisioned.
      </>
    );

  return (
    <Card title="Adjudicate">
      {cmd.data ? outcomeBanner(cmd.data) : null}
      {already ? (
        <Banner tone="info" title="A second reviewer must confirm">
          You've recorded your adjudication decision. A different reviewer has to record the next one.
        </Banner>
      ) : (
        <form className="stack" onSubmit={submit} noValidate>
          <RadioGroup
            legend="Decision"
            name="adjudicate"
            value={decision}
            onChange={setDecision}
            options={[
              { value: "approve", label: "Approve: the debt stands against the publisher", hint: c.dual_approval_required ? "Needs two distinct reviewers." : undefined },
              { value: "reject", label: "Reject: decide for the publisher", hint: "One reviewer resolves it." },
            ]}
          />
          <Textarea label="Notes" hint="Recorded with your decision." value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          <StepUpNote required />
          {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
          {cmd.error && !confirming ? <StaffRejection error={cmd.error} /> : null}
          <div>
            <Button type="submit">Record decision</Button>
          </div>
        </form>
      )}
      <MoneyConfirmDialog
        open={!!confirming}
        title={confirming?.decision === "reject" ? "Decide for the publisher?" : "Uphold the debt against the publisher?"}
        amount={c.claimed_amount}
        currency={c.currency}
        effect={effect}
        pendingSecond={confirming?.decision === "approve" && firstOfTwo && "This case needs two distinct approvals."}
        confirmLabel={confirming?.decision === "approve" && firstOfTwo ? "Record my approval" : "Confirm decision"}
        busy={cmd.isPending}
        error={confirming ? cmd.error : undefined}
       
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && cmd.run(confirming)}
      />
    </Card>
  );
}

function FileAppealForm({ caseId }: { caseId: string }) {
  const ep = useEndpoints();
  const refresh = useCaseRefresh(caseId);
  const [grounds, setGrounds] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand((req: { grounds: string }, opts) => ep.staff.debtCases.fileAppeal(caseId, req.grounds, opts), refresh);
  function submit(e: FormEvent) {
    e.preventDefault();
    const re = reasonError(grounds);
    if (re) return setFormError("State the publisher's grounds for the appeal.");
    setFormError(null);
    cmd.run({ grounds: grounds.trim() });
  }
  return (
    <Card title="File an appeal" subtitle="Filed by a reviewer on the publisher's behalf. The deciders must be different from the adjudicators.">
      <form className="stack" onSubmit={submit} noValidate>
        <Textarea label="Grounds" value={grounds} onChange={(e) => setGrounds(e.target.value)} rows={3} />
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" variant="secondary" loading={cmd.isPending}>
            File appeal
          </Button>
        </div>
      </form>
    </Card>
  );
}

function DecideAppealForm({ c }: { c: StaffDebtCase }) {
  const ep = useEndpoints();
  const refresh = useCaseRefresh(c.case_id);
  const { me } = useStaffSession();
  const [decision, setDecision] = useState<"approve" | "reject" | "">("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<DebtCaseDecisionRequest | null>(null);
  const cmd = useStaffCommand((req: DebtCaseDecisionRequest, opts) => ep.staff.debtCases.decideAppeal(c.case_id, req, opts), () => {
    setConfirming(null);
    refresh();
  });
  const adjudicated = c.approvals.some((a) => a.phase === "adjudication" && a.actor_user_id === me.user_id);
  const voted = c.approvals.some((a) => a.phase === "appeal" && a.actor_user_id === me.user_id);
  const approvalsIn = c.approvals.filter((a) => a.phase === "appeal" && a.decision === "approve").length;
  const firstOfTwo = c.dual_approval_required && approvalsIn === 0;
  const moved = addDecimal(c.debt_recognized ?? "0", c.clawback_amount ?? "0");

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!decision) return setFormError("Choose uphold or deny the appeal.");
    const re = reasonError(notes);
    if (re) return setFormError(re);
    setFormError(null);
    cmd.reset();
    if (decision === "reject") cmd.run({ decision, notes: notes.trim() });
    else setConfirming({ decision, notes: notes.trim() });
  }

  return (
    <Card title="Decide the appeal">
      {cmd.data ? outcomeBanner(cmd.data) : null}
      {adjudicated ? (
        <Banner tone="warning" title="A different reviewer has to decide this appeal">
          You adjudicated this case, so you can't decide its appeal. The server refuses it.
        </Banner>
      ) : voted ? (
        <Banner tone="info" title="A second reviewer must confirm">
          You've recorded your appeal decision. A different reviewer has to record the next one.
        </Banner>
      ) : (
        <form className="stack" onSubmit={submit} noValidate>
          <RadioGroup
            legend="Decision"
            name="debt-appeal"
            value={decision}
            onChange={setDecision}
            options={[
              { value: "approve", label: "Uphold the appeal: reverse the outcome", hint: c.dual_approval_required ? "Moves money; needs two distinct reviewers." : "Moves money." },
              { value: "reject", label: "Deny the appeal: the outcome stands", hint: "One reviewer; nothing moves." },
            ]}
          />
          <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
          <StepUpNote required />
          {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
          {cmd.error && !confirming ? <StaffRejection error={cmd.error} /> : null}
          <div>
            <Button type="submit" loading={cmd.isPending && !confirming}>
              Record decision
            </Button>
          </div>
        </form>
      )}
      <MoneyConfirmDialog
        open={!!confirming}
        title="Uphold the publisher's appeal?"
        amount={moved}
        currency={c.currency}
        effect={
          <>
            Derecognizes <Money value={c.debt_recognized ?? "0"} currency={c.currency} /> of publisher debt (less anything written off)
            and restores <Money value={c.clawback_amount ?? "0"} currency={c.currency} /> of clawed-back entitlement to the publisher. The
            platform funds it; the advertiser keeps their credit.
          </>
        }
        pendingSecond={firstOfTwo && "This case needs two distinct approvals."}
        confirmLabel={firstOfTwo ? "Record my approval" : "Uphold the appeal"}
        busy={cmd.isPending}
        error={confirming ? cmd.error : undefined}
       
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && cmd.run(confirming)}
      />
    </Card>
  );
}
